import api from './api';

/**
 * Get user account settings
 * @returns {Promise<object>} User settings (username, id, email, country, oddsFormat, timezone, language, currency)
 * @throws {Error} 404 if user settings not found (new user)
 */
export const getSettings = async () => {
  const response = await api.get('/api/account/settings');

  return response.data;
};

/**
 * Get user account preferences
 * @returns {Promise<object|null>} User preferences (sports, markets, bet_types, bet_timing, risk, content) or null if not set
 */
export const getPreferences = async () => {
  const response = await api.get('/api/account/preferences');

  return response.data.preferences;
};

/**
 * Update user account settings
 * @param {object} partialSettings - Settings object (partial update supported)
 * @param {string} [partialSettings.username] - Username
 * @param {string} [partialSettings.country] - Country code
 * @param {string} [partialSettings.oddsFormat] - Odds format preference
 * @param {string} [partialSettings.language] - Language code
 * @param {string} [partialSettings.timezone] - Timezone
 * @param {string} [partialSettings.currency] - Currency code
 * @param {object} currentSettings - Current full settings from store (to merge with partial update)
 * @returns {Promise<object>} Updated settings
 */
export const updateSettings = async (partialSettings, currentSettings) => {
  // Merge partial settings with current settings to ensure all required fields are present
  const fullSettings = {
    username: currentSettings.username,
    country: currentSettings.country,
    oddsFormat: currentSettings.oddsFormat,
    language: currentSettings.language,
    timezone: currentSettings.timezone,
    currency: currentSettings.currency,
    ...partialSettings // Override with new values
  };

  const response = await api.post('/api/account/settings', fullSettings);

  return response.data;
};

/**
 * Save user account preferences
 * @param {object} preferences - Preferences object
 * @param {string[]} preferences.sports - Selected sports
 * @param {string[]} preferences.markets - Selected markets
 * @param {string[]} preferences.bet_types - Selected bet types
 * @param {string[]} preferences.bet_timing - Selected bet timing
 * @param {string[]} preferences.risk - Selected risk levels
 * @param {string[]} preferences.content - Selected content preferences
 * @returns {Promise<object>} Updated preferences
 */
export const savePreferences = async (preferences) => {
  const response = await api.post('/api/account/preferences', preferences);

  return response.data;
};

/**
 * Request account deletion (sends verification code to email)
 * @returns {Promise<object>} Response with confirmation that code was sent
 */
export const requestDeleteAccount = async () => {
  const response = await api.post('/api/account/delete/request', {});

  return response.data;
};

/**
 * Confirm account deletion with verification code
 * @param {string} code - 6-digit verification code
 * @returns {Promise<object>} Response confirming account deletion
 */
export const confirmDeleteAccount = async (code) => {
  const response = await api.post('/api/account/delete/confirm', { code });

  return response.data;
};

/**
 * Request email change (sends verification code to old email)
 * @returns {Promise<object>} Response with confirmation that code was sent
 */
export const requestChangeEmail = async () => {
  const response = await api.post('/api/account/change-email/request', {});

  return response.data;
};

/**
 * Confirm old email with verification code
 * @param {string} code - 6-digit verification code
 * @returns {Promise<object>} Response confirming old email verified
 */
export const confirmOldEmail = async (code) => {
  const response = await api.post('/api/account/change-email/confirm-old', { code });

  return response.data;
};

/**
 * Set new email address
 * @param {string} newEmail - New email address
 * @returns {Promise<object>} Response with confirmation that code was sent to new email
 * @throws {Error} 400 if email is already in use
 */
export const setNewEmail = async (newEmail) => {
  const response = await api.post('/api/account/change-email/set-new', { newEmail });

  return response.data;
};

/**
 * Confirm new email with verification code
 * @param {string} code - 6-digit verification code
 * @param {string} deviceId - Device identifier
 * @returns {Promise<object>} Response confirming email change completed
 */
export const confirmNewEmail = async (code, deviceId) => {
  const response = await api.post('/api/account/change-email/confirm-new', { code, deviceId });

  return response.data;
};

/**
 * Authenticate with Google
 * @param {string} googleToken - Google ID token
 * @param {string} deviceId - Device identifier
 * @param timezone
 * @returns {Promise<object>} Auth response with token and refreshToken
 */
export const authenticateWithGoogle = async (googleToken, deviceId, timezone) => {
  const response = await api.post('/api/auth/google', { googleToken, deviceId, timezone });

  return response.data;
};

/**
 * Authenticate with Apple
 * @param {string} appleToken - Apple ID token
 * @param {string} deviceId - Device identifier
 * @param timezone
 * @returns {Promise<object>} Auth response with token and refreshToken
 */
export const authenticateWithApple = async (appleToken, deviceId, timezone) => {
  const response = await api.post('/api/auth/apple', { appleToken, deviceId, timezone });

  return response.data;
};
